import React from 'react';
import Modal from '../../shared/components/Modal';
import { FinanceOperation } from '../../shared/types';
import {
  TYPE_LABELS,
  directionClass,
  directionPrefix,
  formatCryptoAmount,
} from './format';

const CATEGORY_LABELS: Record<string, string> = {
  p2p: 'P2P',
  swap: 'Swap',
  withdrawal: 'Withdrawal',
  deposit: 'Deposit',
  other: 'Other',
};

const CATEGORY_BADGES: Record<string, string> = {
  p2p: 'bg-indigo-500/15 text-indigo-300',
  swap: 'bg-sky-500/15 text-sky-300',
  withdrawal: 'bg-amber-500/15 text-amber-300',
  deposit: 'bg-emerald-500/15 text-emerald-300',
  other: 'bg-slate-500/15 text-slate-300',
};

const DIRECTION_LABELS: Record<string, string> = {
  in: 'Incoming',
  out: 'Outgoing',
  hold: 'Hold / escrow',
};

interface OperationDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  operation: FinanceOperation | null;
  userLabel: string;
  onViewUser?: (userId: number) => void;
  showCategory?: boolean;
}

const Field: React.FC<{ label: string; children: React.ReactNode; className?: string }> = ({
  label,
  children,
  className = '',
}) => (
  <div className={className}>
    <p className="text-xs text-slate-500 mb-1">{label}</p>
    <div className="text-slate-200">{children}</div>
  </div>
);

const OperationDetailModal: React.FC<OperationDetailModalProps> = ({
  isOpen,
  onClose,
  operation,
  userLabel,
  onViewUser,
  showCategory = true,
}) => {
  if (!operation) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Operation #${operation.id}`} size="md">
      <div className="grid grid-cols-2 gap-4">
        <Field label="Date">
          {new Date(operation.created_at).toLocaleString()}
        </Field>
        <Field label="User">
          {onViewUser ? (
            <button
              type="button"
              onClick={() => onViewUser(operation.user_id)}
              className="text-sky-400 hover:text-sky-300 hover:underline transition-colors"
            >
              {userLabel}
            </button>
          ) : (
            <span>{userLabel}</span>
          )}
          <p className="text-xs text-slate-500 font-mono mt-0.5">#{operation.user_id}</p>
        </Field>

        {showCategory && (
          <Field label="Category">
            <span
              className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                CATEGORY_BADGES[operation.category] ?? CATEGORY_BADGES.other
              }`}
            >
              {CATEGORY_LABELS[operation.category] ?? operation.category}
            </span>
          </Field>
        )}

        <Field label="Operation type">
          {TYPE_LABELS[operation.type] ?? operation.type}
        </Field>

        <Field label="Direction">
          {DIRECTION_LABELS[operation.direction] ?? operation.direction}
        </Field>

        <Field label="Currency">
          <span className="text-white font-medium">{operation.currency}</span>
        </Field>

        <Field label="Amount">
          <span className={`font-mono ${directionClass(operation.direction)}`}>
            {directionPrefix(operation.direction)}
            {formatCryptoAmount(operation.amount)} {operation.currency}
          </span>
        </Field>

        <Field label="Balance after">
          <span className="font-mono">{formatCryptoAmount(operation.balance_after)}</span>
        </Field>

        <Field label="Wallet address" className="col-span-2">
          <span className="font-mono text-sm break-all">{operation.wallet_address ?? '—'}</span>
        </Field>

        <Field label="Reference ID" className="col-span-2">
          <span className="font-mono text-sm break-all">{operation.reference_id ?? '—'}</span>
        </Field>

        <Field label="Description" className="col-span-2">
          <span className="text-slate-300">{operation.description ?? '—'}</span>
        </Field>
      </div>
    </Modal>
  );
};

export default OperationDetailModal;
